import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { GlassCard } from '../components/GlassCard';
import { apiFetch } from '../lib/api';
import { Upload, FileSpreadsheet, Download, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const ENTITIES = ['sale', 'product', 'customer', 'purchase'];
const EXPORT_PERIODS = ['30d', '90d', 'ytd', 'all'];

function fmtINR(n) {
  if (n == null) return '₹0';
  try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + Math.round(n); }
}

export default function Integrations() {
  const { token } = useAuth();

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="integrations-page">
      <ImportSection token={token} />
      <ExportSection token={token} />
    </div>
  );
}

/* ── IMPORT SECTION ── */
function ImportSection({ token }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [entity, setEntity] = useState('sales');
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [mapping, setMapping] = useState(null); // array of {our_field, detected_column}
  const [detectedCols, setDetectedCols] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (f) setFile(f);
  };

  const analyze = async () => {
    if (!file || !token) return;
    setAnalyzing(true);
    setMapping(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('target_entity', entity);
      const url = `${BASE}/api/integrations/import/analyze`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail?.code || d.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setSessionId(data.session_id);
      setDetectedCols(data.detected_columns || []);
      setSampleRows(data.sample_rows || []);

      // Convert proposed_mapping dict {our_field: detected_column} → array
      const pm = data.proposed_mapping || {};
      const mappingArr = Object.entries(pm).map(([field, col]) => {
        // Find sample values for this column
        const colIdx = (data.detected_columns || []).indexOf(col);
        const samples = colIdx >= 0
          ? (data.sample_rows || []).slice(0, 3).map(row => row[colIdx])
          : [];
        return { our_field: field, detected_column: col, sample_values: samples };
      });
      setMapping(mappingArr);
      setOverrides({});
    } catch (e) {
      toast.show(e.message || 'Analyze failed', 'error');
    } finally { setAnalyzing(false); }
  };

  const execute = async () => {
    if (!sessionId || !token) return;
    setExecuting(true);
    try {
      const data = await apiFetch('/integrations/import/execute', {
        method: 'POST',
        token,
        body: { session_id: sessionId, mapping_overrides: overrides },
      });
      setResult(data);
      toast.show(`Import complete: ${data.inserted ?? 0} inserted`, 'success');
    } catch (e) {
      toast.show(e.message || 'Execute failed', 'error');
    } finally { setExecuting(false); }
  };

  const reset = () => {
    setFile(null);
    setMapping(null);
    setDetectedCols([]);
    setSampleRows([]);
    setSessionId(null);
    setOverrides({});
    setResult(null);
  };

  return (
    <div>
      <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
        <Upload size={14} className="text-[#84CC16]" /> Import from Excel / CSV
      </h2>
      <GlassCard dark className="p-5 space-y-4">
        {/* Entity picker */}
        <div className="flex gap-2">
          {ENTITIES.map(e => (
            <button key={e} onClick={() => setEntity(e)}
              className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${
                entity === e ? 'bg-[#84CC16]/15 text-[#84CC16] border border-[#84CC16]/25' : 'text-white/40 bg-white/5 border border-white/10 hover:text-white/60'
              }`}
            >{e}</button>
          ))}
        </div>

        {/* Drop zone */}
        {!mapping && !result && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-white/15 rounded-2xl p-8 text-center cursor-pointer hover:border-white/25 transition-colors"
          >
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileDrop} />
            <FileSpreadsheet size={32} className="text-white/30 mx-auto mb-3" />
            {file ? (
              <p className="text-white font-bold text-sm">{file.name} <span className="text-white/40">({(file.size / 1024).toFixed(1)} KB)</span></p>
            ) : (
              <p className="text-white/40 text-sm">Drop a .csv or .xlsx file here, or click to browse</p>
            )}
          </div>
        )}

        {file && !mapping && !result && (
          <button
            data-testid="bridge-import-btn"
            onClick={analyze}
            disabled={analyzing}
            className="px-5 py-2.5 rounded-xl bg-[#84CC16] text-[#0B1220] font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {analyzing ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Upload size={14} />}
            {analyzing ? 'Analyzing…' : 'Analyze File'}
          </button>
        )}

        {/* Mapping review */}
        {mapping && !result && (
          <MappingReview
            mapping={mapping}
            detectedCols={detectedCols}
            overrides={overrides}
            setOverrides={setOverrides}
            onExecute={execute}
            executing={executing}
          />
        )}

        {/* Result */}
        {result && <ImportResult result={result} onReset={reset} />}
      </GlassCard>
    </div>
  );
}

function MappingReview({ mapping, detectedCols, overrides, setOverrides, onExecute, executing }) {
  return (
    <div data-testid="bridge-mapping-card" className="space-y-4">
      <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Mapping Review</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/50 text-xs font-bold uppercase px-3 py-2">Our Field</th>
              <th className="text-left text-white/50 text-xs font-bold uppercase px-3 py-2">Detected Column</th>
              <th className="text-left text-white/50 text-xs font-bold uppercase px-3 py-2">Sample Values</th>
            </tr>
          </thead>
          <tbody>
            {mapping.map((m, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="px-3 py-2 text-white font-semibold">{m.our_field}</td>
                <td className="px-3 py-2">
                  <select
                    value={overrides[m.our_field] ?? m.detected_column ?? ''}
                    onChange={(e) => setOverrides(prev => ({ ...prev, [m.our_field]: e.target.value }))}
                    className="bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-white text-xs font-medium"
                  >
                    <option value="">— skip —</option>
                    {detectedCols.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-white/40 text-xs">{(m.sample_values || []).slice(0, 3).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        data-testid="bridge-execute-btn"
        onClick={onExecute}
        disabled={executing}
        className="px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
      >
        {executing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={14} />}
        {executing ? 'Importing…' : 'Approve & Import'}
      </button>
    </div>
  );
}

function ImportResult({ result, onReset }) {
  const [errOpen, setErrOpen] = useState(false);
  const errors = result.errors || [];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Stat label="Inserted" value={result.inserted ?? 0} color="#10B981" />
        <Stat label="Updated" value={result.updated ?? 0} color="#14B8A6" />
        <Stat label="Skipped" value={result.skipped ?? 0} color="#F59E0B" />
        <Stat label="Errors" value={errors.length} color={errors.length > 0 ? '#EF4444' : '#10B981'} />
      </div>
      {errors.length > 0 && (
        <div>
          <button onClick={() => setErrOpen(!errOpen)} className="flex items-center gap-1 text-red-400 text-xs font-bold">
            <AlertCircle size={12} /> {errors.length} error{errors.length !== 1 ? 's' : ''}
            {errOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {errOpen && (
            <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
              {errors.map((e, i) => <p key={i} className="text-red-300/80 text-xs">{typeof e === 'string' ? e : JSON.stringify(e)}</p>)}
            </div>
          )}
        </div>
      )}
      <button onClick={onReset} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 font-bold text-sm hover:bg-white/10 transition-colors">
        Import another file
      </button>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10">
      <p className="text-white/40 text-[10px] font-bold uppercase">{label}</p>
      <p className="font-extrabold text-lg tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}

/* ── EXPORT SECTION ── */
function ExportSection({ token }) {
  return (
    <div>
      <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
        <Download size={14} className="text-[#84CC16]" /> Export
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ExportCard title="Tally Export" desc="Download Tally-compatible XML vouchers" btnLabel="Download Tally XML" testId="bridge-export-tally-btn" token={token} endpoint="tally" ext="xml" mime="application/xml" />
        <ExportCard title="Zoho Export" desc="Download Zoho-compatible CSV invoices" btnLabel="Download Zoho CSV" testId="bridge-export-zoho-btn" token={token} endpoint="zoho" ext="csv" mime="text/csv" />
      </div>
    </div>
  );
}

function ExportCard({ title, desc, btnLabel, testId, token, endpoint, ext, mime }) {
  const toast = useToast();
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    try {
      const url = `${BASE}/api/integrations/export/${endpoint}?period=${period}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: mime },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${endpoint}_export_${period}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.show(`${title} downloaded`, 'success');
    } catch (e) {
      toast.show(e.message || 'Download failed', 'error');
    } finally { setLoading(false); }
  };

  return (
    <GlassCard dark className="p-5 space-y-3">
      <h3 className="text-white font-bold text-sm">{title}</h3>
      <p className="text-white/40 text-xs">{desc}</p>
      <div className="flex gap-1.5">
        {EXPORT_PERIODS.map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${
              period === p ? 'bg-white/10 text-white border border-white/20' : 'text-white/40 hover:text-white/60'
            }`}
          >{p.toUpperCase()}</button>
        ))}
      </div>
      <button
        data-testid={testId}
        onClick={download}
        disabled={loading}
        className="px-5 py-2.5 rounded-xl bg-[#84CC16] text-[#0B1220] font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50 flex items-center gap-2"
      >
        {loading ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Download size={14} />}
        {loading ? 'Downloading…' : btnLabel}
      </button>
    </GlassCard>
  );
}
